import type {ObservableQuery} from '@apollo/client';
import type {
  CreateInvestmentAdjustmentMutationVariables,
  InvestmentGapsQuery,
} from '@transactions/graphql';

export type Gap = InvestmentGapsQuery['investmentGaps'][number];

/** A choice in the picker: what the gap was, and the adjustment that records it. */
export type Choice = {
  key: string;
  label: string;
  /** The adjustment kind it records. */
  kind: string;
  /** Where it lands in the split, as a label. */
  lands: string;
  /** Records shares (a share gap's choices) or cash. */
  shares: boolean;
  /** A default note, so the history says what it was. */
  note: string;
};

const CASH_CHOICES: Choice[] = [
  {key: 'dividend', label: 'Dividend', kind: 'income', lands: 'Income', shares: false, note: 'Dividend'},
  {key: 'interest', label: 'Interest', kind: 'income', lands: 'Income', shares: false, note: 'Interest'},
  {key: 'gain', label: 'Capital gain', kind: 'gain', lands: 'Realized', shares: false, note: 'Capital gain'},
  {key: 'deposit', label: 'Deposit', kind: 'transfer', lands: 'Saved', shares: false, note: 'Deposit'},
  {key: 'withdrawal', label: 'Withdrawal', kind: 'transfer', lands: 'Saved', shares: false, note: 'Withdrawal'},
  {key: 'fee', label: 'Fee', kind: 'fee', lands: 'Fees', shares: false, note: 'Fee'},
  {key: 'writeoff', label: 'Write off', kind: 'writeoff', lands: 'Unexplained', shares: false, note: ''},
];

const SHARE_CHOICES: Choice[] = [
  {key: 'trade', label: 'Trade', kind: 'trade', lands: 'Basis and cash', shares: true, note: 'Trade'},
  {key: 'transfer', label: 'Transfer in kind', kind: 'transfer', lands: 'Saved, at value', shares: true, note: 'Transfer'},
  {key: 'corporate', label: 'Split or corporate action', kind: 'corporate', lands: 'Shares only', shares: true, note: 'Corporate action'},
  {key: 'writeoff', label: 'Write off', kind: 'writeoff', lands: 'Unexplained', shares: true, note: ''},
];

/** A gap is about shares (a security) or cash. */
export const isShareGap = (gap: Gap) => gap.securityId != null;

/** The choices that fit a gap: cash in or out, or shares. */
export const choicesFor = (gap: Gap): Choice[] =>
  isShareGap(gap)
    ? SHARE_CHOICES
    : CASH_CHOICES.filter((choice) =>
        (gap.cash ?? 0) > 0
          ? !['withdrawal', 'fee'].includes(choice.key)
          : !['dividend', 'interest', 'gain', 'deposit'].includes(choice.key),
      );

/**
 * For each account the checks don't balance, the last observation at which
 * every check did, before its earliest open gap (null if none did). A gap
 * still settling (a trade that may yet post) doesn't count. Accounts that
 * balance aren't listed.
 */
export const balancedThroughByAccount = (gaps: Gap[]): Map<string, string | null> => {
  const through = new Map<string, string | null>();
  for (const gap of gaps) {
    if (gap.status !== 'unbalanced') continue;
    const day = gap.balancedThrough ?? null;
    if (!through.has(gap.accountId)) {
      through.set(gap.accountId, day);
      continue;
    }
    // The earliest wins, and never balanced (null) is earliest of all.
    const current = through.get(gap.accountId) ?? null;
    if (current != null && (day == null || day < current)) through.set(gap.accountId, day);
  }
  return through;
};

/** An unbalanced account's status line. */
export const balanceStatusText = (balancedThrough: string | null) =>
  balancedThrough == null ? 'Not balanced' : `Balanced through ${balancedThrough}`;

/**
 * How a record or delete refreshes the screen: it finishes only once the
 * gaps are fresh, so a resolved gap can't be recorded twice meanwhile. A
 * refresh that fails doesn't fail the write (which happened); the query
 * keeps its own error, and the screen holds off until it's fresh.
 */
export const RECONCILE_REFETCH = {
  refetchQueries: ['InvestmentGaps', 'InvestmentAdjustments', 'InvestmentPeriods'],
  awaitRefetchQueries: true,
  onQueryUpdated: (query: ObservableQuery) => query.refetch().catch(() => undefined),
};

/**
 * The choice the server's suggestion names, else the last that fits (a
 * write-off). Income is a dividend unless the payout it matched was interest.
 */
export const suggestedChoice = (gap: Gap): Choice => {
  const choices = choicesFor(gap);
  const interest = gap.suggestion.subtype?.includes('interest') ?? false;
  const byKind = choices.find(
    (choice) =>
      choice.kind === gap.suggestion.kind &&
      (choice.kind !== 'transfer' || choice.shares === isShareGap(gap)) &&
      (choice.kind !== 'income' || (choice.key === 'interest') === interest),
  );
  return byKind ?? choices[choices.length - 1];
};

/**
 * Gaps to show: a cash gap paired with a share gap is the same trade, shown
 * once, on the share gap (whose suggestion carries the cash).
 */
export const visibleGaps = (gaps: Gap[]): Gap[] => gaps.filter((gap) => gap.pairedSecurityId == null);

/**
 * Which of a record's fields close its gap rather than widen it. It must be
 * dated after the observation before the gap opened and on or before the
 * one that measures it (else the checks never see it). A record dated d
 * moves every check from d on, so its amount must close each of them
 * without overshooting: the gap's way, and no more than it was there. A
 * trade's cash answers to its paired cash, which hasn't moved since; with
 * none paired, a trade moves no cash (any would open a cash gap).
 */
export const closesGap = (
  gap: Gap,
  choice: Choice,
  fields: {date: string; cents: number | null; quantity: number | null},
): {date: boolean; shares: boolean; cash: boolean} => {
  // Within a check: its way round, and no bigger, give or take what the
  // checks themselves count as the same (a cent of cash, as the server's
  // sameCash; float noise in shares).
  const within = (value: number, check: number, shares: boolean) =>
    Math.sign(value) === Math.sign(check) &&
    Math.abs(value) <= (shares ? Math.abs(check) * (1 + 1e-9) : Math.abs(check) + 1);
  const reached = gap.history.filter((step) => step.day >= fields.date);
  const closesEach = (value: number | null, shares: boolean) =>
    value == null || value === 0 || reached.every((step) => within(value, step.amount, shares));
  return {
    date: fields.date > gap.after && fields.date <= gap.day,
    shares: !choice.shares || closesEach(fields.quantity, true),
    cash: choice.shares
      ? fields.cents == null ||
        fields.cents === 0 ||
        (gap.suggestion.cash != null && within(fields.cents, gap.suggestion.cash, false))
      : closesEach(fields.cents, false),
  };
};

/** The adjustment that records a choice for a gap, from the sheet's fields. */
export const adjustmentFor = (
  gap: Gap,
  choice: Choice,
  fields: {date: string; cash: number | null; quantity: number | null; note: string},
): CreateInvestmentAdjustmentMutationVariables => ({
  accountId: gap.accountId,
  date: fields.date,
  kind: choice.kind,
  // A cash choice names the payout's security only when it's the choice suggested.
  securityId: choice.shares
    ? (gap.securityId ?? undefined)
    : choice.key === suggestedChoice(gap).key
      ? (gap.suggestion.securityId ?? undefined)
      : undefined,
  quantity: choice.shares ? (fields.quantity ?? undefined) : undefined,
  // A corporate action moves no money; anything else records the cash.
  cash: choice.kind === 'corporate' || fields.cash == null ? undefined : fields.cash,
  note: fields.note.trim() === '' ? undefined : fields.note.trim(),
  // The gap it closes, as seen: the server refuses it if that's changed.
  gapSince: gap.since,
  gapDay: gap.day,
});

/** The one-step record for a confident suggestion. */
export const confidentAdjustment = (gap: Gap): CreateInvestmentAdjustmentMutationVariables => {
  const choice = suggestedChoice(gap);
  return adjustmentFor(gap, choice, {
    date: gap.suggestion.date,
    cash: gap.suggestion.cash ?? gap.cash ?? null,
    quantity: gap.suggestion.quantity ?? gap.quantity ?? null,
    note: choice.kind === 'writeoff' ? (gap.suggestion.evidence ?? 'Rounding') : choice.note,
  });
};
